import React, { useRef, useState, useEffect } from 'react';
import { initI18n, useTranslation } from '../../shared/i18n/index';
import PublicPageLayout from '../../shared/components/PublicPageLayout';

// Feature Card Component
const FeatureCard = ({ icon, iconBg, title, description }) => (
  <div className="col-md-6 mb-4">
    <div
      className="bg-white rounded-3 p-4 h-100"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
    >
      <div className="d-flex">
        <div
          className="rounded-2 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
          style={{ width: '48px', height: '48px', backgroundColor: iconBg }}
        >
          <i className={`bi ${icon}`} style={{ fontSize: '20px', color: '#667eea' }}></i>
        </div>
        <div>
          <h4 className="fw-bold mb-2" style={{ fontSize: '18px', color: '#111827' }}>
            {title}
          </h4>
          <p className="mb-0" style={{ fontSize: '15px', color: '#4b5563', lineHeight: '24px' }}>
            {description}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Benefit Card Component
const BenefitCard = ({ icon, iconBg, title, description, gradient }) => (
  <div
    className="rounded-4 p-4 text-white h-100"
    style={{
      background: gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}
  >
    <div
      className="rounded-2 d-flex align-items-center justify-content-center mb-3"
      style={{
        width: '56px',
        height: '56px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <i className={`bi ${icon}`} style={{ fontSize: '24px', color: 'white' }}></i>
    </div>
    <h4 className="fw-bold mb-2" style={{ fontSize: '20px' }}>
      {title}
    </h4>
    <p className="mb-0" style={{ fontSize: '15px', lineHeight: '24px', opacity: 0.9 }}>
      {description}
    </p>
  </div>
);

// For Parents Section
const ForParentsSection = ({ t, programCount }) => (
  <section
    className="py-5"
    style={{
      background: 'linear-gradient(142.52deg, #faf5ff 0%, #eff6ff 70.71%)',
    }}
  >
    <div className="container py-4">
      {/* Section Badge */}
      <div className="text-center mb-4">
        <span
          className="badge fw-semibold"
          style={{
            background: 'linear-gradient(to right, #667eea, #764ba2)',
            padding: '8px 16px',
            borderRadius: '9999px',
            fontSize: '14px',
          }}
        >
          {t('features.forParents.badge')}
        </span>
      </div>

      {/* Section Header */}
      <div className="text-center mb-5">
        <h2
          className="fw-bold mb-3"
          style={{ fontSize: '36px', color: '#111827', lineHeight: '44px' }}
        >
          {t('features.forParents.headline')}
        </h2>
        <p style={{ fontSize: '18px', color: '#4b5563', maxWidth: '600px', margin: '0 auto' }}>
          {t('features.forParents.subheadline')}
        </p>
      </div>

      <div className="row">
        {/* Feature Cards Column */}
        <div className="col-lg-8">
          <div className="row">
            <FeatureCard
              icon="bi-people-fill"
              iconBg="#dbeafe"
              title={t('features.forParents.createFamily.title')}
              description={t('features.forParents.createFamily.description')}
            />
            <FeatureCard
              icon="bi-journal-bookmark-fill"
              iconBg="#dcfce7"
              title={t('features.forParents.setCurriculum.title')}
              description={t('features.forParents.setCurriculum.description', { program_count: programCount })}
            />
            <FeatureCard
              icon="bi-bullseye"
              iconBg="#fef3c7"
              title={t('features.forParents.setGoals.title')}
              description={t('features.forParents.setGoals.description')}
            />
            <FeatureCard
              icon="bi-graph-up-arrow"
              iconBg="#f3e8ff"
              title={t('features.forParents.monitor.title')}
              description={t('features.forParents.monitor.description')}
            />
          </div>
        </div>

        {/* Benefit Card Column */}
        <div className="col-lg-4 mb-4">
          <BenefitCard
            icon="bi-clock-history"
            title={t('features.forParents.benefit.title')}
            description={t('features.forParents.benefit.description')}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          />
        </div>
      </div>
    </div>
  </section>
);

// For Kids Section
const ForKidsSection = ({ t, programCount }) => (
  <section className="py-5 bg-white">
    <div className="container py-4">
      {/* Section Badge */}
      <div className="text-center mb-4">
        <span
          className="badge fw-semibold"
          style={{
            background: 'linear-gradient(to right, #22c55e, #16a34a)',
            padding: '8px 16px',
            borderRadius: '9999px',
            fontSize: '14px',
          }}
        >
          {t('features.forKids.badge')}
        </span>
      </div>

      {/* Section Header */}
      <div className="text-center mb-5">
        <h2
          className="fw-bold mb-3"
          style={{ fontSize: '36px', color: '#111827', lineHeight: '44px' }}
        >
          {t('features.forKids.headline')}
        </h2>
        <p style={{ fontSize: '18px', color: '#4b5563', maxWidth: '600px', margin: '0 auto' }}>
          {t('features.forKids.subheadline')}
        </p>
      </div>

      <div className="row">
        {/* Benefit Card Column */}
        <div className="col-lg-4 mb-4 order-lg-2">
          <BenefitCard
            icon="bi-rocket-takeoff-fill"
            title={t('features.forKids.benefit.title')}
            description={t('features.forKids.benefit.description')}
            gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
          />
        </div>

        {/* Feature Cards Column */}
        <div className="col-lg-8 order-lg-1">
          <div className="row">
            <FeatureCard
              icon="bi-controller"
              iconBg="#dcfce7"
              title={t('features.forKids.gameBased.title')}
              description={t('features.forKids.gameBased.description')}
            />
            <FeatureCard
              icon="bi-collection-fill"
              iconBg="#dbeafe"
              title={t('features.forKids.programs.title', { program_count: programCount })}
              description={t('features.forKids.programs.description')}
            />
            <FeatureCard
              icon="bi-trophy-fill"
              iconBg="#fef3c7"
              title={t('features.forKids.motivation.title')}
              description={t('features.forKids.motivation.description')}
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = ({ loginPath, t }) => (
  <section
    className="py-5"
    style={{
      background: 'linear-gradient(16.88deg, #667eea 50%, #764ba2 120.71%)',
    }}
  >
    <div className="container py-5">
      <div className="text-center text-white">
        <h2 className="fw-bold mb-4" style={{ fontSize: '42px', lineHeight: '50px' }}>
          {t('features.cta.title')}
        </h2>
        <p
          className="mb-4 mx-auto"
          style={{ fontSize: '20px', maxWidth: '700px', color: '#f3f4f6', lineHeight: '30px' }}
        >
          {t('features.cta.description')}
        </p>
        <a
          href={loginPath}
          className="btn btn-light btn-lg fw-semibold px-5"
          style={{ borderRadius: '8px', color: '#667eea' }}
        >
          {t('features.cta.button')}
        </a>
      </div>
    </div>
  </section>
);

// Main Features Page Component
const FeaturesPage = ({ locale = 'vi', loginPath = '/login', flashNotice, flashAlert }) => {
  // Initialize i18n only once on mount
  const i18nInitialized = useRef(false);
  if (!i18nInitialized.current) {
    initI18n(locale);
    i18nInitialized.current = true;
  }
  const { t } = useTranslation();

  // State for program count from API
  const [programCount, setProgramCount] = useState(12);

  // Fetch program count from API
  useEffect(() => {
    fetch('/api/public/features')
      .then((res) => res.json())
      .then((data) => {
        if (data.program_count) {
          setProgramCount(data.program_count);
        }
      })
      .catch(() => {
        // Silently fail - use default
      });
  }, []);

  return (
    <PublicPageLayout
      loginPath={loginPath}
      flashNotice={flashNotice}
      flashAlert={flashAlert}
      t={t}
    >
      {/* Hero Section */}
      <section className="py-5 bg-white">
        <div className="container py-4">
          <div className="text-center">
            <h1
              className="fw-bold mb-3"
              style={{ fontSize: '48px', color: '#111827', lineHeight: '56px' }}
            >
              {t('features.title')}
            </h1>
            <p
              className="mx-auto"
              style={{ fontSize: '20px', color: '#4b5563', maxWidth: '700px' }}
            >
              {t('features.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* For Parents Section */}
      <ForParentsSection t={t} programCount={programCount} />

      {/* For Kids Section */}
      <ForKidsSection t={t} programCount={programCount} />

      {/* CTA Section */}
      <CTASection loginPath={loginPath} t={t} />
    </PublicPageLayout>
  );
};

export default FeaturesPage;
